import React, { useState, useEffect } from 'react';
import { CollapsibleSection } from '../common/CollapsibleSection';
import { HistogramChart } from '../charts/HistogramChart';
import { ExampleTrials } from './ExampleTrials';
import { TrialResult, HistogramConfig } from '../../types';

interface SimulationDisplayProps {
  isRunning: boolean;
  progress: {
    completed: number;
    total: number;
  };
  currentTrialResults: TrialResult[];
  histogramConfig: HistogramConfig;
  peopleNames: string[];
  onAbort?: () => void;
  className?: string;
}

function chooseExamples(currentTrialResults: TrialResult[], maxExamples: number = 3): TrialResult[] {

  maxExamples = Math.min(maxExamples, currentTrialResults.length);

  // Pick examples basically randomly, but try to include a mix of successful and 
  // unsuccessful trials.
  const successfulTrials = currentTrialResults.filter(r => r.finalBalance > 0 && !r.wasOverdrawn);
  const overdrawnTrials = currentTrialResults.filter(r => r.finalBalance > 0 && r.wasOverdrawn);
  const negativeEndingTrials = currentTrialResults.filter(r => r.finalBalance <= 0 || r.wasOverdrawn);
  
  const examples = new Set<TrialResult>();

  if(successfulTrials.length > 0) {
    const which = Math.floor(Math.random() * successfulTrials.length);
    examples.add(successfulTrials[which]);
  }
  if(overdrawnTrials.length > 0) {
    const which = Math.floor(Math.random() * overdrawnTrials.length);
    examples.add(overdrawnTrials[which]);
  }
  if (negativeEndingTrials.length > 0) {
    const which = Math.floor(Math.random() * negativeEndingTrials.length);
    examples.add(negativeEndingTrials[which]);
  }

  while(examples.size < maxExamples) {
    const which = Math.floor(Math.random() * currentTrialResults.length);
    examples.add(currentTrialResults[which]);
  }

  return Array.from(examples);
}

/**
 * Component for displaying simulation progress and live results
 */
export const SimulationDisplay: React.FC<SimulationDisplayProps> = ({
  isRunning,
  progress,
  currentTrialResults,
  histogramConfig,
  peopleNames,
  onAbort,
  className = ''
}) => {
  const [displayedResults, setDisplayedResults] = useState<TrialResult[]>([]);
  const [exampleTrials, setExampleTrials] = useState<TrialResult[]>([]);

  // Update displayed results only when simulation completes
  useEffect(() => {
    if (!isRunning && currentTrialResults.length > 0) {
      // Update results when simulation completes
      setDisplayedResults([...currentTrialResults]);
      
      const maxExamples = 3;
      const examples = chooseExamples(currentTrialResults, maxExamples);
      setExampleTrials(examples);
    }
  }, [isRunning, currentTrialResults]);

  const getStatusMessage = (): string => {
    if (isRunning) {
      return `Running simulation... (${progress.completed} / ${progress.total} trials complete)`;
    } else if (progress.completed > 0) {
      return `Simulation complete. ${progress.completed} trials run.`;
    } else {
      return 'Simulation not yet run.';
    }
  };

  const finalBalances = displayedResults.map(result => result.finalBalance);

  return (
    <CollapsibleSection
      title="Simulation"
      defaultCollapsed={!isRunning && progress.completed === 0}
      className={`simulation-display ${className}`}
    >
      <div className="simulation-content">
        {/* Status and Progress */}
        <div className="simulation-status">
          <p className="status-message">{getStatusMessage()}</p>
          
          {isRunning && (
            <div className="simulation-controls">
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
              
              {onAbort && (
                <button
                  type="button"
                  onClick={onAbort}
                  className="abort-simulation-btn"
                >
                  Abort Simulation
                </button>
              )}
            </div>
          )}
        </div>

        {/* Live Histogram */}
        {displayedResults.length > 0 && (
          <div className="simulation-chart">
            <HistogramChart
              config={histogramConfig}
              data={finalBalances}
              title="Trial Outcome Distribution (Total Final Balance)"
              className="live-histogram"
            />
          </div>
        )}

        {/* Example Trials */}
        {exampleTrials.length > 0 && (
          <div className="example-trials-section">
            <ExampleTrials
              trialsData={exampleTrials}
              peopleNames={peopleNames}
              maxTrials={4}
            />
          </div>
        )}

        {/* Placeholder when no data */}
        {displayedResults.length === 0 && !isRunning && (
          <div className="no-simulation-data">
            <p>Simulation results will appear here.</p>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};
